/**
 * Tax Efficiency / Dividend / Additional metrics.
 * Values come from API only — section templates hold keys/labels.
 *
 * Expected API shape (any partial is fine):
 * {
 *   taxEfficiency?: { title?, items?: [{ key?, label, value, tone? }] },
 *   dividendIncome?: { title?, items?: [...] },
 *   additionalMetrics?: { title?, items?: [...] }
 * }
 */

export const TAX_VALUE_TONES = {
  positive: "#45AC70",
  negative: "#C40000",
  neutral: "#FFFFFF",
};

/** Section titles + row keys/labels only (values come from API). */
const TAX_EFFICIENCY_SECTIONS = {
  taxEfficiency: {
    title: "Tax Efficiency - LTCG Profile",
    items: [
      { key: "estimatedGrossGain", label: "Estimated Gross Gain" },
      { key: "ltcgExemptionLimit", label: "LTCG Exemption Limit" },
      { key: "taxableCapitalGain", label: "Taxable Capital Gain" },
      { key: "ltcgTax", label: "LTCG Tax @ 12.5%" },
      { key: "dividendTax", label: "Dividend Tax (Slab)" },
      { key: "netPostTaxReturn", label: "Net Post-Tax Return" },
    ],
  },
  dividendIncome: {
    title: "Dividend Income - 1 Year",
    // Stock rows are API-driven only (no static holdings list).
    items: [],
  },
  additionalMetrics: {
    title: "Additional 1-year metrics",
    items: [
      { key: "upMarketCapture", label: "Up-Market Capture" },
      { key: "downMarketCapture", label: "Down-Market Capture" },
      { key: "battingAverage", label: "Batting Average" },
      { key: "gainLossRatio", label: "Gain/Loss Ratio" },
      { key: "skewness", label: "Skewness of Returns" },
      { key: "kurtosis", label: "Kurtosis" },
    ],
  },
};

const pickValue = (apiSection, item) => {
  if (!apiSection || !item?.key) return "—";
  const raw =
    apiSection[item.key] ??
    apiSection[item.key.replace(/([A-Z])/g, "_$1").toLowerCase()];
  if (raw == null || raw === "") return "—";
  if (typeof raw === "object") {
    return raw.value ?? raw.label ?? "—";
  }
  return String(raw);
};

const mergeSection = (sectionDef, apiSection) => {
  if (!sectionDef) return null;

  const apiItems = Array.isArray(apiSection?.items) ? apiSection.items : null;
  const hasApiSection = apiSection && typeof apiSection === "object";

  if (!hasApiSection) return null;

  if (apiItems) {
    if (!apiItems.length) return null;
    return {
      title: apiSection.title || sectionDef.title,
      items: apiItems.map((item, index) => ({
        key: item.key || `item_${index}`,
        label: item.label || "—",
        value: item.value ?? "—",
        tone: item.tone || "neutral",
        bold: !!item.bold,
      })),
    };
  }

  // Flat key/value API → only include rows that have a value
  const items = sectionDef.items
    .map((item) => {
      const value = pickValue(apiSection, item);
      if (value === "—") return null;

      const base = {
        key: item.key,
        label: item.label,
        value,
        tone: "neutral",
        bold: false,
      };

      if (apiSection?.[item.key]?.tone) {
        base.tone = apiSection[item.key].tone;
      }
      if (apiSection?.[item.key]?.bold != null) {
        base.bold = !!apiSection[item.key].bold;
      }

      return base;
    })
    .filter(Boolean);

  if (!items.length) return null;

  return {
    title: apiSection.title || sectionDef.title,
    items,
  };
};

/**
 * Map taxEfficiency API onto section labels. Values from API only.
 * Returns null when no payload so the section can be hidden.
 */
export const normalizeTaxEfficiency = (apiSection) => {
  if (!apiSection || typeof apiSection !== "object") return null;

  const taxEfficiency = mergeSection(
    TAX_EFFICIENCY_SECTIONS.taxEfficiency,
    apiSection.taxEfficiency || apiSection.ltcgProfile,
  );
  const dividendIncome = mergeSection(
    TAX_EFFICIENCY_SECTIONS.dividendIncome,
    apiSection.dividendIncome || apiSection.dividends,
  );
  const additionalMetrics = mergeSection(
    TAX_EFFICIENCY_SECTIONS.additionalMetrics,
    apiSection.additionalMetrics ||
      apiSection.oneYearMetrics ||
      apiSection.performanceMetrics,
  );

  if (!taxEfficiency && !dividendIncome && !additionalMetrics) return null;

  return {
    taxEfficiency,
    dividendIncome,
    additionalMetrics,
  };
};
